import { inject, Injectable } from '@angular/core';
import { objectDistinctUntilChanged, rmaShareReplay } from '@rma/generic/rxjs/operators';
import { catchError, combineLatest, Observable, of, startWith, Subject, switchMap, take } from 'rxjs';
import { ContextSelectorService } from './context-selector.service';
import { CurrentUserService } from './current-user.service';
import { CurrentUser } from './domain/current-user.models';
import { CurrentProfileContext, SelectedContext } from './domain/profile-context.models';
import { ProfileContextApiService } from './profile-context-api.service';

/**
 * ProfileContextService
 *
 * Provides the current profile context for the authenticated user, combining user and selected profile information.
 *
 * - Exposes `currentProfileContext$`, an observable that emits a {@link CurrentProfileContext} object representing the user's current profile context,
 *   or `null` when no profile resolves (no user, no selected context, or the lookup fails). Consumers own how they handle that absence.
 * - Allows forcing a re-resolution of the profile context via `reloadProfileContext()`.
 *
 *  @remarks
 * - This makes more sense when we have a shared user library across public, dashboard and promoter. At the moment it is very thin
 */
@Injectable({
  providedIn: 'root',
})
export class ProfileContextService {
  private readonly currentUserService = inject(CurrentUserService);
  private readonly contextSelector = inject(ContextSelectorService);
  private readonly profileContextApi = inject(ProfileContextApiService);

  private readonly forceReload$ = new Subject<void>();

  public readonly currentProfileContext$: Observable<CurrentProfileContext | null> = this.getCurrentProfileContext();

  public reloadProfileContext(): void {
    this.forceReload$.next();
  }

  private getCurrentProfileContext() {
    return combineLatest({
      user: this.currentUserService.currentUser$,
      selectedContext: this.contextSelector.selectedContext$.pipe(objectDistinctUntilChanged()),
      reload: this.forceReload$.asObservable().pipe(startWith(undefined)), // Ensure the observable emits an initial value
    }).pipe(
      switchMap(({ user, selectedContext }) => this.mapUserProfile(user, selectedContext)),
      rmaShareReplay(),
    );
  }

  private mapUserProfile(user: CurrentUser | null, selectedContext: SelectedContext | undefined): Observable<CurrentProfileContext | null> {
    if (!user || !selectedContext) {
      return of(null);
    }

    return this.profileContextApi.getProfileContextDetails(selectedContext.profileType, selectedContext.profileCode).pipe(
      take(1),
      // No resolvable profile is a first-class kernel output; each app projection re-owns its own absence policy.
      catchError(() => of(null)),
    );
  }
}
